import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  Chip,
  Menu,
  MenuItem,
  Checkbox,
  Divider,
  Tooltip,
} from "@mui/material";
import "../../analytics/process/process.scss";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";

import { useState, useMemo, useCallback, useEffect } from "react";
import "./review.scss";
import TemplateChangeSummaryTooltip from "./TemplateChangeSummaryTooltip";
import partialBatchWarningIcon from "../../../assets/icons/partial-batch-warning.svg";
import ApplicationStatusChip from "../../../components/StatusChip/ApplicationStatusChip";
import { getApplicationStatusStyle } from "../../../constants/statusStyles";

const normalizeTemplateVersion = (version) =>
  String(version ?? "")
    .trim()
    .replace(/^version[\s_-]*/i, "")
    .replace(/^v(?=\d)/i, "")
    .toLowerCase();

const TemplateVersionCell = ({ batchVersion, currentVersion }) => {
  const normalizedBatchVersion = normalizeTemplateVersion(batchVersion);
  const normalizedCurrentVersion = normalizeTemplateVersion(currentVersion);
  const isCurrentVersion =
    normalizedBatchVersion &&
    normalizedCurrentVersion &&
    normalizedBatchVersion === normalizedCurrentVersion;
  const hasVersionMismatch =
    normalizedBatchVersion &&
    normalizedCurrentVersion &&
    normalizedBatchVersion !== normalizedCurrentVersion;

  return (
    <Box display="flex" alignItems="center" gap={0.75} whiteSpace="nowrap">
      <Box
        component="span"
        sx={{
          minWidth: 28,
          px: 0.75,
          py: 0.25,
          borderRadius: "4px",
          backgroundColor: isCurrentVersion ? "#C8FACC" : "#DADADA",
          color: isCurrentVersion ? "#37B349" : "#000000",
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        {batchVersion ?? "-"}
      </Box>
      {hasVersionMismatch && (
        <Tooltip
          arrow
          title="This batch uses an older template. Use Copy Batch to update it to the current version."
        >
          <CompareArrowsRoundedIcon
            aria-label="Template version differs from current version"
            sx={{ color: "#667085", fontSize: 18 }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default function ParameterTable({
  data,
  onView,
  tableHeaders,
  openData,
  moreOption,
  handleMenuOption,
  selectedCheck,
  handleCheckBox,
  is_multi_approve,
  sortConfig,
  handleSort,
  returnTo,
  canViewBatchData = false,
  canViewCopiedBatches = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [rowData, setRowData] = useState(null);
  const open = Boolean(anchorEl);

  // ✅ HANDLE SORT

  // ✅ SORT ICON
  const renderSortIcon = (column) => {
    const isActive = sortConfig.sort_by === column;

    // 👇 common style
    const baseStyle = {
      fontSize: 18, // ⬆ bigger height
      ml: 0.5,
      color: "#154D8F", // 🔵 your theme blue
    };

    if (!isActive) {
      return (
        <ArrowUpwardIcon
          sx={{
            ...baseStyle,
            opacity: 0.3, // faint for inactive
          }}
        />
      );
    }

    return sortConfig.sort_order === "asc" ? (
      <ArrowUpwardIcon sx={baseStyle} />
    ) : (
      <ArrowDownwardIcon sx={baseStyle} />
    );
  };

  const handleClick = (event, data) => {
    if (!moreOption?.length || returnTo) return;

    setAnchorEl(event.currentTarget);
    setRowData(data);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const allIds = useMemo(
    () =>
      data?.data?.records
        ?.filter((r) => r?.status !== "Approved" && r?.status !== "Pending")
        .map((r) => r?.batch_id)
        .filter(Boolean),
    [data?.data?.records],
  );

  const allSelected =
    allIds?.length > 0 && selectedCheck?.length === allIds?.length;

  const isIndeterminate =
    selectedCheck?.length > 0 && selectedCheck?.length < allIds?.length;

  const selectAll = useCallback(
    (checked) => {
      if (checked) {
        handleCheckBox(allIds);
      } else {
        handleCheckBox([]);
      }
    },
    [handleCheckBox, allIds],
  );

  const toggleSelect = useCallback(
    (batchId) => {
      if (!batchId) return;
      if (selectedCheck.includes(batchId)) {
        handleCheckBox(selectedCheck.filter((id) => id !== batchId));
      } else {
        handleCheckBox([...selectedCheck, batchId]);
      }
    },
    [handleCheckBox, selectedCheck],
  );

  return (
    <Box
      width="100%"
      sx={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}
    >
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          height: "100%",
          maxHeight: "100%",
          overflow: "auto",
          boxShadow: "none",
        }}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: 1480,
            tableLayout: "auto",
            "& .MuiTableCell-root": {
              whiteSpace: "nowrap",
            },
          }}
        >
          <TableHead className="table-header">
            <TableRow>
              {is_multi_approve ? (
                <TableCell
                  className="table-header-cell bulk-approval-checkbox-cell"
                  sx={{ minWidth: "max-content" }}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={isIndeterminate}
                    onChange={(e) => selectAll(e.target.checked)}
                  />
                  {selectedCheck.length > 0 ? ` ${selectedCheck.length}` : ""}
                </TableCell>
              ) : (
                ""
              )}
              <TableCell className="table-header-cell" sx={{ minWidth: 80 }}>
                Sr No.
              </TableCell>
              <TableCell
                className="table-header-cell partial-batch-icon-header-cell"
                aria-label="Partial batch indicator"
                sx={{ width: 24, minWidth: 24, pl: 0, pr: 0 }}
              />
              <TableCell
                className="table-header-cell batch-id-header-cell"
                sx={{ whiteSpace: "nowrap", pl: "6px" }}
              >
                <Box
                  component="span"
                  sx={{ display: "block", transform: "translateX(-10px)" }}
                >
                  Batch ID
                </Box>
              </TableCell>
              <TableCell className="table-header-cell">Version</TableCell>

              {/* ✅ SORTABLE HEADERS */}

              <TableCell
                className="table-header-cell"
                onClick={() => handleSort("batch_date")}
                sx={{ cursor: "pointer" }}
              >
                <Box display="flex" alignItems="center">
                  Date of Manufacture
                  {renderSortIcon("batch_date")}
                </Box>
              </TableCell>

              <TableCell
                className="table-header-cell"
                onClick={() => handleSort("approved_at")}
                sx={{ cursor: "pointer" }}
              >
                <Box display="flex" alignItems="center">
                  Approved On
                  {renderSortIcon("approved_at")}
                </Box>
              </TableCell>

              <TableCell className="table-header-cell">Status</TableCell>

              <TableCell className="table-header-cell">Uploaded By</TableCell>

              <TableCell
                className="table-header-cell"
                onClick={() => handleSort("created_at")}
                sx={{ cursor: "pointer" }}
              >
                <Box display="flex" alignItems="center">
                  Uploaded On
                  {renderSortIcon("created_at")}
                </Box>
              </TableCell>

              <TableCell className="table-header-cell">Approved By</TableCell>

              {canViewCopiedBatches && (
                <TableCell className="table-header-cell">Copied</TableCell>
              )}

              <TableCell className="table-header-cell">Source</TableCell>

              <TableCell className="table-header-cell">Action</TableCell>
            </TableRow>
          </TableHead>

          <Divider sx={{ border: "none", height: "2px" }} />

          <TableBody
            sx={{
              "& .MuiTableRow-root": { height: 48 },
              "& .MuiTableCell-root": { py: 0.5 },
            }}
          >
            {data?.data?.records?.map((row, index) => (
              <TableRow
                key={row.batch_id}
                sx={{
                  backgroundColor:
                    row.is_updated && !row.is_copied_batch ? "#EEEEEE" : "",
                }}
              >
                {is_multi_approve ? (
                  <TableCell className="bulk-approval-checkbox-cell">
                    <Checkbox
                      checked={
                        row.status === "Approved" || row.status === "Pending"
                          ? true
                          : selectedCheck.includes(row.batch_id)
                      }
                      disabled={
                        row.status === "Approved" || row.status === "Pending"
                      }
                      onChange={() => toggleSelect(row?.batch_id)}
                      size="small"
                      sx={{ p: 0, pl: 0.5 }}
                    />
                  </TableCell>
                ) : (
                  ""
                )}
                <TableCell>
                  {data?.data?.page_size * (data?.data?.page - 1) + index + 1}
                </TableCell>
                <TableCell sx={{ width: 24, minWidth: 24, pl: 0, pr: 0 }}>
                  {row.is_partial_batch === true && (
                    <Tooltip title="Partial batch" arrow>
                      <Box
                        component="img"
                        src={partialBatchWarningIcon}
                        alt="Partial batch"
                        sx={{ width: 24, height: 24, display: "block" }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell
                  className="batch-id-data-cell"
                  sx={{ whiteSpace: "nowrap", pl: "6px" }}
                >
                  <Typography
                    color="primary"
                    sx={{
                      cursor: canViewBatchData ? "pointer" : "default",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        textDecoration: canViewBatchData ? "underline" : "none",
                      },
                    }}
                    onClick={() => canViewBatchData && onView(row)}
                  >
                    {row.batch_id}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 14 }}>
                  <TemplateVersionCell
                    batchVersion={row.template_version}
                    currentVersion={data?.data?.current_template_version}
                  />
                </TableCell>

                <TableCell sx={{ fontSize: 14 }}>{row.batch_date}</TableCell>

                <TableCell sx={{ fontSize: 14 }}>{row.approved_at}</TableCell>

                <TableCell sx={{ fontSize: 14 }}>
                  {getApplicationStatusStyle(row.status) ? (
                    <ApplicationStatusChip
                      status={row.status}
                      sx={{ minWidth: 120 }}
                    />
                  ) : (
                    <Chip
                      label={row.status}
                      className="review-status-chip status-chip status-chip-color-gray"
                    />
                  )}
                </TableCell>

                <TableCell sx={{ fontSize: 14 }}>{row.uploaded_by}</TableCell>

                <TableCell sx={{ fontSize: 14 }}>{row.created_at}</TableCell>

                <TableCell sx={{ fontSize: 14 }}>{row.approved_by}</TableCell>

                {canViewCopiedBatches && (
                  <TableCell sx={{ fontSize: 14 }}>
                    {row.is_copied_batch === true ? "Yes" : "No"}
                  </TableCell>
                )}

                <TableCell
                  sx={{
                    fontSize: 14,
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                  }}
                >
                  <Tooltip title={row?.source || ""} arrow>
                    <Typography noWrap>{row?.source}</Typography>
                  </Tooltip>
                </TableCell>

                <TableCell sx={{ textAlign: "center", minWidth: 96 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexWrap: "nowrap",
                      gap: 0.25,
                    }}
                  >
                    <TemplateChangeSummaryTooltip row={row} />
                    <IconButton
                      size="small"
                      onClick={(e) => handleClick(e, row)}
                      disabled={!moreOption?.length || returnTo}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    elevation={0}
                    PaperProps={{
                      sx: {
                        border: "1px solid #E0E0E0",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {moreOption?.map((option) => (
                      <MenuItem
                        key={option}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClose();
                          handleMenuOption(rowData, option);
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Menu>
                </TableCell>
              </TableRow>
            ))}

            {!data?.loader && data?.data?.records?.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={
                    (is_multi_approve ? 14 : 13) -
                    (canViewCopiedBatches ? 0 : 1)
                  }
                  align="center"
                >
                  <Typography color="textSecondary">No data found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
